/* ==========================================================================
   EVOLT — theme behaviour
   Drives every interactive hook in the theme:
     [data-add-to-cart]  product cards
     [data-pick] / [data-add-stack]  build-your-own-stack builder
     [data-add-bundle]   bundle explorer + featured bundles
     [data-cart-drawer]  slide-out cart, line quantities, totals
     [data-nav-toggle]   mobile navigation
     [data-cascade]      scroll reveal
   ========================================================================== */
(function () {
  'use strict';

  var routes = window.routes || {};
  var CART_ADD = routes.cart_add_url || '/cart/add';
  var CART_CHANGE = routes.cart_change_url || '/cart/change';

  /* ---------- audience memory -------------------------------------------- */

  // Checkout branding is global and cannot vary per visitor, so the cart page
  // is the last surface we control. Remember which side of the store the
  // customer shopped and carry that palette through to /cart.
  var AUDIENCE_KEY = 'evolt-audience';

  function readAudience() {
    try { return localStorage.getItem(AUDIENCE_KEY); } catch (e) { return null; }
  }

  function writeAudience(value) {
    try { localStorage.setItem(AUDIENCE_KEY, value); } catch (e) {}
  }

  function rememberAudienceFromPage() {
    var cls = document.body.className;
    if (/\btemplate-index\b|\btpl-pickleball/.test(cls) || /\btpl-court\b/.test(cls)) {
      writeAudience('pickleball');
    } else if (/\btpl-general\b|\btpl-shop\b/.test(cls)) {
      writeAudience('general');
    }
  }

  // Fallback only — theme.liquid applies this inline in <head> on the cart page
  // so the palette is correct on first paint.
  function applyAudiencePalette() {
    if (!document.body.classList.contains('template-cart')) return;
    var audience = readAudience();
    if (audience === 'pickleball' || audience === 'general') {
      document.documentElement.classList.add('audience-' + audience);
    }
  }

  /* ---------- helpers ---------------------------------------------------- */

  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }

  // Mirrors Shopify's money filter closely enough for live subtotals.
  function money(cents) {
    var fmt = window.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return parts.join('.');
        case 'amount_no_decimals': return parts[0];
        case 'amount_with_comma_separator': return parts[0].replace(/,/g, '.') + ',' + parts[1];
        case 'amount_no_decimals_with_comma_separator': return parts[0].replace(/,/g, '.');
        default: return parts.join('.');
      }
    });
  }

  function toast(message) {
    var el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('show');
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.classList.remove('show'); }, 2600);
  }

  function busy(btn, on, label) {
    if (!btn) return;
    if (on) {
      btn._label = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = label || 'Adding…';
    } else {
      btn.disabled = false;
      if (btn._label) btn.innerHTML = btn._label;
    }
  }

  /* ---------- cart ------------------------------------------------------- */

  function addToCart(items) {
    return fetch(CART_ADD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error(body.description || body.message || 'Could not add to cart.');
        return body;
      });
    });
  }

  function changeLine(key, quantity) {
    return fetch(CART_CHANGE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (res) { return res.json(); });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); });
  }

  function lineHTML(item) {
    var img = item.image
      ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') + '" alt="" width="56" height="56">'
      : '<span style="width:56px;height:56px;background:var(--ink-3);border-radius:var(--radius);"></span>';
    var variant = item.variant_title && item.variant_title.indexOf('Default') === -1
      ? '<div class="cart-line__meta">' + item.variant_title + '</div>'
      : '';

    return '<div class="cart-line" data-cart-line data-line-key="' + item.key + '">' +
      img +
      '<div>' +
        '<div class="cart-line__name">' + item.product_title + '</div>' + variant +
        '<div class="cart-line__qty">' +
          '<button data-qty-minus aria-label="Decrease">−</button>' +
          '<span data-qty>' + item.quantity + '</span>' +
          '<button data-qty-plus aria-label="Increase">+</button>' +
        '</div>' +
      '</div>' +
      '<div class="cart-line__price">' + money(item.final_line_price) + '</div>' +
    '</div>';
  }

  function renderCart(cart) {
    $$('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });

    var subtotal = $('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = money(cart.total_price);

    var foot = $('[data-cart-foot]');
    if (foot) foot.hidden = cart.item_count === 0;

    var items = $('[data-cart-items]');
    if (!items) return;

    if (cart.item_count === 0) {
      items.innerHTML = '<div class="cart-empty"><p>Your cart is empty.</p>' +
        '<a href="/collections/all" class="btn btn--ghost" style="margin-top:14px;">Start shopping</a></div>';
    } else {
      items.innerHTML = cart.items.map(lineHTML).join('');
    }
  }

  function refreshCart() {
    return getCart().then(renderCart);
  }

  /* ---------- cart drawer ------------------------------------------------ */

  var drawer = $('[data-cart-drawer]');
  var overlay = $('[data-drawer-overlay]');

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('open');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('open');
    document.documentElement.style.overflow = '';
  }

  if (overlay) overlay.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ---------- delegated clicks ------------------------------------------- */

  document.addEventListener('click', function (e) {
    var el;

    /* close cart */
    if (e.target.closest('[data-cart-close]')) {
      closeDrawer();
      return;
    }

    /* open cart from the header bag */
    el = e.target.closest('[data-cart-toggle], [data-cart-open]');
    if (el && drawer) {
      e.preventDefault();
      refreshCart().then(openDrawer);
      return;
    }

    /* single product card */
    el = e.target.closest('[data-add-to-cart]');
    if (el) {
      e.preventDefault();
      var id = el.getAttribute('data-variant-id');
      if (!id) return;
      busy(el, true);
      addToCart([{ id: Number(id), quantity: 1 }])
        .then(function () {
          toast((el.getAttribute('data-product-title') || 'Item') + ' added to cart');
          return refreshCart();
        })
        .then(openDrawer)
        .catch(function (err) { toast(err.message); })
        .then(function () { busy(el, false); });
      return;
    }

    /* pre-built bundle */
    el = e.target.closest('[data-add-bundle]');
    if (el) {
      e.preventDefault();

      // Prefer the per-line variants so any flavour the customer picked is
      // honoured; fall back to the server-rendered defaults.
      var card = el.closest('[data-bundle]');
      var ids = card ? $$('[data-bundle-item]', card).map(function (li) {
        return Number(li.getAttribute('data-variant-id'));
      }).filter(Boolean) : [];

      if (!ids.length) {
        ids = (el.getAttribute('data-variant-ids') || '')
          .split(',')
          .map(function (v) { return Number(v.trim()); })
          .filter(Boolean);
      }
      if (!ids.length) { toast('This bundle has no products yet.'); return; }

      busy(el, true);
      addToCart(ids.map(function (v) { return { id: v, quantity: 1 }; }))
        .then(function () {
          var code = el.getAttribute('data-discount-code');
          if (code) applyDiscount(code);
          toast((el.getAttribute('data-bundle-name') || 'Bundle') + ' added to cart');
          return refreshCart();
        })
        .then(openDrawer)
        .catch(function (err) { toast(err.message); })
        .then(function () { busy(el, false); });
      return;
    }

    /* cart line quantity */
    el = e.target.closest('[data-qty-plus], [data-qty-minus]');
    if (el) {
      var line = el.closest('[data-cart-line]');
      if (!line) return;
      var qtyEl = $('[data-qty]', line);
      var next = Number(qtyEl.textContent) + (el.hasAttribute('data-qty-plus') ? 1 : -1);
      changeLine(line.getAttribute('data-line-key'), Math.max(0, next)).then(renderCart);
      return;
    }

    /* mobile nav */
    if (e.target.closest('[data-nav-toggle]')) {
      var header = $('.site-header');
      if (header) header.classList.toggle('nav-open');
      return;
    }
  });

  // Discount codes are applied via the storefront's discount endpoint so the
  // code is already on the cart by the time the customer reaches checkout.
  function applyDiscount(code) {
    return fetch('/discount/' + encodeURIComponent(code), { method: 'GET' }).catch(function () {});
  }

  /* ---------- flavour pickers -------------------------------------------- */

  // Bundle line: swapping a flavour changes which variant is added and, when
  // flavours are priced differently, what the bundle costs.
  function repriceBundle(card) {
    if (!card) return;
    var pct = Number(card.getAttribute('data-discount-percent')) || 0;
    var full = $$('[data-bundle-item]', card).reduce(function (sum, li) {
      return sum + (Number(li.getAttribute('data-price')) || 0);
    }, 0);
    var saved = Math.round(full * pct / 100);

    var was = $('[data-bundle-was]', card);
    var now = $('[data-bundle-now]', card);
    var savedEl = $('[data-bundle-saved]', card);
    if (was) was.textContent = money(full);
    if (now) now.textContent = money(full - saved);
    if (savedEl) savedEl.textContent = money(saved);
  }

  // Swap a thumbnail to the chosen variant's image. Variants without their own
  // image fall back to the product shot, which is already in data-image.
  function swapImage(img, opt) {
    if (!img || !opt) return;
    var src = opt.getAttribute('data-image');
    if (!src || img.getAttribute('src') === src) return;
    img.setAttribute('src', src);
    img.removeAttribute('srcset');
    img.setAttribute('alt', opt.text.trim());
  }

  document.addEventListener('change', function (e) {
    var sel = e.target;

    if (sel.matches('[data-card-variant]')) {
      var productCard = sel.closest('.card');
      var cardOption = sel.options[sel.selectedIndex];
      if (!productCard || !cardOption) return;

      var addButton = $('[data-add-to-cart]', productCard);
      var currentPrice = $('[data-card-current-price]', productCard);
      var priceWrap = $('[data-card-price]', productCard);
      var comparePrice = $('[data-card-compare-price]', productCard);
      var compareText = cardOption.getAttribute('data-compare-price') || '';
      var available = cardOption.getAttribute('data-available') === 'true';

      if (addButton) {
        addButton.setAttribute('data-variant-id', cardOption.value);
        addButton.disabled = !available;
        addButton.textContent = available ? 'Add' : 'Sold out';
      }
      if (currentPrice) currentPrice.textContent = cardOption.getAttribute('data-price') || '';
      if (priceWrap) {
        if (compareText) {
          if (!comparePrice) {
            comparePrice = document.createElement('s');
            comparePrice.setAttribute('data-card-compare-price', '');
            priceWrap.insertBefore(comparePrice, currentPrice);
          }
          comparePrice.textContent = compareText;
          comparePrice.hidden = false;
        } else if (comparePrice) {
          comparePrice.hidden = true;
        }
      }

      var cardImage = $('[data-card-product-image]', productCard);
      swapImage(cardImage, cardOption);
      if (cardImage) cardImage.alt = cardOption.getAttribute('data-alt') || cardOption.text.trim();

      var optionUrl = cardOption.getAttribute('data-url');
      if (optionUrl) {
        $$('[data-card-link]', productCard).forEach(function (link) { link.href = optionUrl; });
      }
      var optionTitle = cardOption.getAttribute('data-title');
      var cardTitle = $('[data-card-title]', productCard);
      if (optionTitle && cardTitle) cardTitle.textContent = optionTitle;
      var optionDescription = cardOption.getAttribute('data-description');
      var cardDescription = $('[data-card-description]', productCard);
      if (optionDescription && cardDescription) cardDescription.textContent = optionDescription;
      return;
    }

    if (sel.matches('[data-bundle-flavor]')) {
      var li = sel.closest('[data-bundle-item]');
      var opt = sel.options[sel.selectedIndex];
      if (!li || !opt) return;
      li.setAttribute('data-variant-id', opt.value);
      li.setAttribute('data-price', opt.getAttribute('data-price') || '0');
      swapImage($('[data-bundle-thumb]', li), opt);
      repriceBundle(sel.closest('[data-bundle]'));
      return;
    }

    if (sel.matches('[data-pick-flavor]')) {
      var pick = sel.closest('[data-pick]');
      var o = sel.options[sel.selectedIndex];
      if (!pick || !o) return;
      var price = Number(o.getAttribute('data-price')) || 0;
      pick.setAttribute('data-variant-id', o.value);
      pick.setAttribute('data-price', String(price));
      pick.setAttribute('data-name', (pick.getAttribute('data-product-title') || '') + ' — ' + o.text.trim());

      var priceEl = $('[data-pick-price]', pick);
      if (priceEl) priceEl.textContent = money(price);
      swapImage($('[data-pick-image]', pick), o);

      // Keep the stack panel honest if this product is already in the stack.
      var builder = pick.closest('[data-section-type="bundle-builder"]');
      if (builder && builder._render) builder._render();
    }
  });

  // A <select> inside a clickable tile must not toggle the tile.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-pick-flavor], [data-bundle-flavor]')) e.stopPropagation();
  }, true);

  /* ---------- product detail dialog (stack builder) ----------------------- */

  var activePick = null;

  function modalFor(pick) {
    var section = pick.closest('[data-section-type="bundle-builder"]');
    return section ? $('[data-pick-modal]', section) : null;
  }

  // Keeps the dialog's button label honest while it is open.
  function syncModal(pick) {
    if (!activePick || activePick !== pick) return;
    var modal = modalFor(pick);
    var btn = modal && $('[data-pk-toggle]', modal);
    if (btn) btn.textContent = pick.getAttribute('aria-pressed') === 'true' ? 'Remove from stack' : 'Add to stack';
  }

  function openPickModal(pick) {
    var modal = modalFor(pick);
    if (!modal) return;
    activePick = pick;

    var img = $('[data-pk-image]', modal);
    var tileImg = $('[data-pick-image]', pick);
    if (img) {
      if (tileImg) {
        img.src = tileImg.getAttribute('src');
        img.alt = pick.getAttribute('data-product-title') || '';
        img.hidden = false;
      } else {
        img.hidden = true;
      }
    }

    var title = $('[data-pk-title]', modal);
    if (title) title.textContent = pick.getAttribute('data-name') || pick.getAttribute('data-product-title') || '';

    var price = $('[data-pk-price]', modal);
    if (price) price.textContent = money(Number(pick.getAttribute('data-price')) || 0);

    var desc = $('[data-pk-desc]', modal);
    var tpl = pick.querySelector('template[data-pick-desc]');
    if (desc) desc.innerHTML = tpl ? tpl.innerHTML : '<p>No description yet.</p>';

    var link = $('[data-pk-link]', modal);
    if (link) {
      var url = pick.getAttribute('data-product-url');
      if (url) { link.href = url; link.hidden = false; } else { link.hidden = true; }
    }

    syncModal(pick);
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';

    var closer = $('[data-pick-modal-close]', modal);
    if (closer) closer.focus();
  }

  function closePickModal() {
    $$('[data-pick-modal]').forEach(function (m) { m.hidden = true; });
    document.documentElement.style.overflow = '';
    if (activePick) {
      var opener = $('[data-pick-open]', activePick);
      if (opener) opener.focus();
    }
    activePick = null;
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-pick-modal-close]')) {
      e.preventDefault();
      closePickModal();
      return;
    }
    if (e.target.closest('[data-pk-toggle]')) {
      e.preventDefault();
      if (!activePick) return;
      var section = activePick.closest('[data-section-type="bundle-builder"]');
      if (section && section._toggle) section._toggle(activePick);
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && activePick) closePickModal();
  });

  /* ---------- build-your-own-stack --------------------------------------- */

  function initBuilder(root) {
    var picks = $$('[data-pick]', root);
    if (!picks.length) return;

    var t1 = Number(root.getAttribute('data-t1')) || 0;
    var t2 = Number(root.getAttribute('data-t2')) || 0;
    var t3 = Number(root.getAttribute('data-t3')) || 0;
    var code = root.getAttribute('data-discount-code');

    var vis = $('[data-stack-vis]', root);
    var empty = $('[data-stack-empty]', root);
    var meter = $('[data-stack-meter]', root);
    var addBtn = $('[data-add-stack]', root);

    function tierFor(count) {
      if (count >= 5) return t3;
      if (count === 4) return t2;
      if (count === 3) return t1;
      return 0;
    }

    function selected() {
      return picks.filter(function (p) { return p.getAttribute('aria-pressed') === 'true'; });
    }

    function render() {
      var chosen = selected();
      var count = chosen.length;
      var subtotal = chosen.reduce(function (sum, p) { return sum + Number(p.getAttribute('data-price') || 0); }, 0);
      var pct = tierFor(count);
      var savings = Math.round(subtotal * pct / 100);

      // plates
      $$('.stack-plate', vis).forEach(function (n) { n.remove(); });
      chosen.forEach(function (p) {
        var plate = document.createElement('span');
        plate.className = 'stack-plate';
        plate.textContent = p.getAttribute('data-name') || '';
        vis.appendChild(plate);
      });
      if (empty) empty.hidden = count > 0;

      // meter
      if (meter) {
        if (count === 0) {
          meter.innerHTML = 'Add <b>3</b> more to unlock ' + t1 + '% off.';
        } else if (count < 3) {
          meter.innerHTML = 'Add <b>' + (3 - count) + '</b> more to unlock ' + t1 + '% off.';
        } else if (count === 3) {
          meter.innerHTML = '<b>' + t1 + '% off</b> unlocked — add <b>1</b> more for ' + t2 + '%.';
        } else if (count === 4) {
          meter.innerHTML = '<b>' + t2 + '% off</b> unlocked — add <b>1</b> more for ' + t3 + '%.';
        } else {
          meter.innerHTML = '<b>' + t3 + '% off</b> unlocked — the best tier.';
        }
      }

      // tier chips
      $$('[data-tier-hint]', root).forEach(function (hint) {
        var tier = Number(hint.getAttribute('data-tier-hint'));
        var on = (tier === 1 && count === 3) || (tier === 2 && count === 4) || (tier === 3 && count >= 5);
        hint.classList.toggle('active', on);
      });

      // totals
      var set = function (sel, text) { var n = $(sel, root); if (n) n.textContent = text; };
      set('[data-stack-items]', String(count));
      set('[data-stack-subtotal]', money(subtotal));
      set('[data-stack-savings]', money(savings));
      set('[data-stack-total]', money(subtotal - savings));

      if (addBtn) addBtn.disabled = count === 0;
    }

    function toggle(p) {
      var on = p.getAttribute('aria-pressed') !== 'true';
      p.setAttribute('aria-pressed', on ? 'true' : 'false');

      var addBtn = $('[data-pick-add]', p);
      if (addBtn) {
        addBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var title = p.getAttribute('data-product-title') || 'this product';
        addBtn.setAttribute('aria-label', (on ? 'Remove ' : 'Add ') + title + (on ? ' from your stack' : ' to your stack'));
      }
      render();
      syncModal(p);
    }

    picks.forEach(function (p) {
      // The + button is the only thing that changes the stack.
      var addBtn = $('[data-pick-add]', p);
      if (addBtn) {
        addBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          toggle(p);
        });
      }

      // The tile body opens the detail dialog instead.
      var opener = $('[data-pick-open]', p);
      if (opener) {
        opener.addEventListener('click', function (e) {
          e.preventDefault();
          openPickModal(p);
        });
        opener.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            openPickModal(p);
          }
        });
      }
    });

    root._toggle = toggle;

    // Exposed so a flavour change can refresh the totals.
    root._render = render;

    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var chosen = selected();
        if (!chosen.length) return;

        busy(addBtn, true);
        addToCart(chosen.map(function (p) { return { id: Number(p.getAttribute('data-variant-id')), quantity: 1 }; }))
          .then(function () {
            if (code && tierFor(chosen.length) > 0) applyDiscount(code);
            toast('Stack of ' + chosen.length + ' added to cart');
            chosen.forEach(function (p) { p.setAttribute('aria-pressed', 'false'); });
            render();
            return refreshCart();
          })
          .then(openDrawer)
          .catch(function (err) { toast(err.message); })
          .then(function () { busy(addBtn, false); });
      });
    }

    render();
  }

  /* ---------- scroll reveal ---------------------------------------------- */

  function initReveal() {
    var nodes = $$('[data-cascade]');
    if (!nodes.length || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---------- boot ------------------------------------------------------- */

  function boot() {
    rememberAudienceFromPage();
    applyAudiencePalette();
    $$('[data-section-type="bundle-builder"]').forEach(initBuilder);
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-bind when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var builder = e.target.querySelector('[data-section-type="bundle-builder"]');
    if (builder) initBuilder(builder);
    initReveal();
  });
})();
